import React from 'react';

import './Button.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warn';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Bootstrap Icons class. */
  iconLeft?: string;
  /** Bootstrap Icons class. */
  iconRight?: string;
  block?: boolean;
  children?: React.ReactNode;
}

/**
 * `primary` — mint fill (one bright action per surface).
 * `secondary` — transparent + subtle border.
 * `ghost` — minimal frame; toolbars and dense contexts.
 * `danger` — coral fill; destructive actions.
 *
 * @example
 *   <Button variant="primary" iconLeft="bi-plus-lg">New view</Button>
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      iconLeft,
      iconRight,
      block,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={[
        'wb-btn',
        `wb-btn--${variant}`,
        `wb-btn--${size}`,
        block ? 'wb-btn--block' : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {iconLeft && <i className={`bi ${iconLeft}`} aria-hidden />}
      {children !== undefined && <span className="wb-btn__label">{children}</span>}
      {iconRight && <i className={`bi ${iconRight}`} aria-hidden />}
    </button>
  )
);
Button.displayName = 'Button';

export default Button;
